// Year-by-year picture of someone's pay through the IRS Jovem years: what a normal month brings home, what the
// bonus months add, the year's totals, and the IRS refund to expect the following year.

import { r2, sum } from './money.js';
import { computeSlips } from './model.js';
import { jovemSchedule } from './tax/irsJovem.js';
import { taxParamsFor } from './tax/params.js';
import { ageOn } from './calendar.js';
import { payslip, withPlanDefaults } from './payslip.js';
import { versionAt } from './state.js';

const pad = (n) => String(n).padStart(2, '0');

function yearRows(state, personId, fromYear, toYear) {
  const plain = { ...state, months: {} }; // expectations only: no sick days etc.
  const computed = computeSlips(plain, fromYear, toYear)[personId];
  const rows = [];
  for (let y = fromYear; y <= toYear; y++) {
    const yr = computed.years[y];
    const slips = Array.from({ length: 12 }, (_, i) => computed.slips[`${y}-${pad(i + 1)}`]);
    const full = slips.filter((x) => x.plan && x.slip.netBank > 0 && !x.slip.notes.some((n) => n.startsWith('Part month')));
    const normal = full.find((x) => x.slip.subsidies === 0) || null;
    const normalNet = normal ? r2(normal.slip.netBank - normal.slip.allowance) : null;
    const bonusMonths = full.filter((x) => x.slip.subsidies > 0).map((x) => ({
      month: slips.indexOf(x) + 1,
      extra: normal ? r2(x.slip.netBank - x.slip.allowance - normalNet) : null,
    }));
    rows.push({
      year: y,
      jovem: yr.jovem,
      estimatedTax: yr.estimatedTax,
      taxYear: yr.taxYear,
      months: yr.months,
      normal: normal ? { net: normalNet, allowance: normal.slip.allowance, meal: normal.slip.mealCard, irs: normal.slip.irs, ss: normal.slip.ss, gross: normal.slip.taxable } : null,
      bonusMonths,
      annual: { gross: yr.taxable, ss: yr.ss, irs: yr.irs, netBank: yr.netBank, meal: yr.meal, allowance: yr.allowance },
      settlement: yr.settlement,
    });
  }
  return rows;
}

// Rows from the first IRS Jovem year (or the start) until the year after the benefit ends
export function jovemProjection(state, personId, { toYear = null } = {}) {
  const p = state.people.find((x) => x.id === personId);
  if (!p) return { rows: [], schedule: [] };
  const firstPlanYear = Number((p.plans[0]?.from || state.settings.startMonth).slice(0, 4));
  const { params } = taxParamsFor(firstPlanYear, state.taxYears);
  const schedule = jovemSchedule(p.irsJovem, p.birthDate, params.irsJovem?.percentages, params.irsJovem?.maxAge);
  const from = Math.max(firstPlanYear, Number(state.settings.startMonth.slice(0, 4)));
  const lastScheduled = schedule.length ? schedule[schedule.length - 1].year : from + 1;
  const to = Math.max(from + 1, lastScheduled, toYear || 0);
  return { rows: yearRows(state, personId, Math.min(from, schedule[0]?.year ?? from), to), schedule };
}

// "What if this year doesn't count?" — e.g. being claimed as a dependant in the year the job starts.
// Compares take-home pay + refunds over the same years for both cases.
export function compareFirstYear(state, personId) {
  const p = state.people.find((x) => x.id === personId);
  const j = p?.irsJovem;
  if (!p || !j?.enabled || !Number.isInteger(Number(j.firstYear))) return null;
  const first = Number(j.firstYear);
  // Only possible while the parents can still claim them: 25 or younger on 31 December of that year
  const age = p.birthDate ? ageOn(p.birthDate, `${first}-12-31`) : null;
  if (age !== null && age > 25) return null;
  const alt = {
    ...state,
    people: state.people.map((x) => (x.id === personId ? { ...x, irsJovem: { ...j, skipYears: [...new Set([...(j.skipYears || []), first])] } } : x)),
  };
  const reach = Math.max(...[jovemProjection(state, personId), jovemProjection(alt, personId)].map((x) => x.rows[x.rows.length - 1]?.year || first));
  const a = jovemProjection(state, personId, { toYear: reach });
  const b = jovemProjection(alt, personId, { toYear: reach });
  const years = [...new Set([...a.rows, ...b.rows].map((r) => r.year))].sort();
  const total = (rows) => sum(rows.filter((r) => years.includes(r.year)).map((r) => r.annual.netBank + r.annual.meal + (r.settlement?.refund || 0)));
  const ta = total(a.rows);
  const tb = total(b.rows);
  return {
    firstYear: first,
    years: [years[0], years[years.length - 1]],
    asIs: { total: ta, rows: a.rows, schedule: a.schedule },
    skipFirst: { total: tb, rows: b.rows, schedule: b.schedule },
    difference: r2(tb - ta),
  };
}

/* ── The offer letter, side by side with IRS Jovem ─────────
   Same lines as the letter: a normal month (no June/December pay), the lunch allowance for 21 days, June and
   December, and the year. The first column is the letter itself when its IRS figures were typed in (otherwise the
   same salary without IRS Jovem); the others are each IRS Jovem stage, worked out with the official tables. */

function stagesOf(person, params) {
  const schedule = jovemSchedule(person.irsJovem, person.birthDate, params.irsJovem?.percentages, params.irsJovem?.maxAge).filter((x) => x.eligible);
  const out = [];
  for (const x of schedule) {
    const last = out[out.length - 1];
    if (last && last.pct === x.pct && last.years[last.years.length - 1] === x.year - 1) last.years.push(x.year);
    else out.push({ pct: x.pct, index: x.index, years: [x.year] });
  }
  return out;
}

export function offerComparison(state, personId, currentMonth) {
  const person = state.people.find((x) => x.id === personId);
  if (!person) return null;
  const raw = versionAt(person.plans, currentMonth) || person.plans.find((x) => x.from > currentMonth) || person.plans[0];
  const plan = withPlanDefaults(raw);
  if (!raw || plan.mode !== 'employee') return null;
  const full = { ...plan, startDate: '', endDate: '' }; // a whole year at the job
  const holidayOpts = { carnival: !!person.holidays?.carnival, extra: person.holidays?.extra || [] };
  const meal = plan.mealType === 'none' ? 0 : r2((plan.mealPerDay || 0) * 21);
  const ssRate = (plan.ssRate ?? 11) / 100;
  const pctOf = (d, base, commission) => (d.of === 'base' ? base : d.of === 'gross' ? base + commission : commission);
  const deductionList = (plan.deductions || []).filter((d) => d.enabled !== false && (d.pct || d.fixed))
    .map((d) => ({ label: d.label || 'Deduction', amount: r2((d.fixed || 0) + pctOf(d, plan.base, plan.commission) * ((d.pct || 0) / 100)) }));

  const column = (label, sub, year, jovem) => {
    const { params } = taxParamsFor(year, state.taxYears);
    const ctx = { params, jovem, holidayOpts, appliedAtSource: true };
    const skip = new Set([plan.subsidies.holidayMonth, plan.subsidies.christmasMonth, plan.yearlyBonus ? plan.yearlyBonusMonth : 0]);
    let normalKey = null;
    for (let m = 1; m <= 12 && !normalKey; m++) {
      const key = `${year}-${pad(m)}`;
      if (skip.has(m) || (params.withholdingAdjustments || []).some((a) => a.month === key)) continue;
      normalKey = key;
    }
    const n = payslip(full, normalKey, {}, ctx);
    const bank = r2(n.netBank - n.allowance);
    const extras = [plan.subsidies.holidayMonth, plan.subsidies.christmasMonth].map((m) => {
      const s = payslip(full, `${year}-${pad(m)}`, {}, ctx);
      return r2(s.netBank - s.allowance - bank);
    });
    return {
      label, sub, letter: false, base: plan.base, commission: plan.commission, gross: r2(plan.base + plan.commission + meal),
      irs: n.irs, ss: n.ss, deductions: deductionList.map((d) => n.lines.find((l) => l.key === 'deduction' && l.label === d.label)?.amount * -1 || 0),
      bank, meal, month: r2(bank + meal), extra: extras[0] === extras[1] ? extras[0] : null, extras, year: Math.round(bank * 12 + extras[0] + extras[1] + meal * 11),
    };
  };

  const columns = [];
  const thisYear = Number(currentMonth.slice(0, 4));
  const { params } = taxParamsFor(thisYear, state.taxYears);
  const stages = stagesOf(person, params);
  const span = (st) => (st.years.length > 1 ? `${st.years[0]}–${st.years[st.years.length - 1]}` : String(st.years[0]));
  const extraA = plan.allowance || 0; // Despesas: the letter's "Plano Salarial" lines add them

  if (typeof plan.letterIrsBase === 'number') {
    // The letter's own IRS figures. IRS Jovem leaves only the taxed share of them: 0% the first year, then 25%,
    // 50% and 75%. Everything else is worked out exactly as the letter does.
    const ib0 = plan.letterIrsBase;
    const iv0 = typeof plan.letterIrsCommission === 'number' ? plan.letterIrsCommission : 0;
    const ssBase = r2(plan.base * ssRate);
    const ssCom = plan.commissionSS === false ? 0 : r2(plan.commission * ssRate);
    const dedTotal = r2(deductionList.reduce((a, d) => a + d.amount, 0));
    const share = (amount, taxed) => Math.round(Math.round(amount * 100) * taxed) / 100; // in cents, half up
    const make = (label, sub, taxed, letter) => {
      const irsBase = share(ib0, taxed);
      const irsCommission = share(iv0, taxed);
      const b = r2(plan.base - irsBase - ssBase); // each of the 14 base payments
      const c = r2(plan.commission - irsCommission - ssCom - dedTotal); // each of the 12 commission payments
      // The letter column shows the letter's own yearly figure when we have it (it doesn't round IRS to the cent)
      const annual = letter && typeof plan.letterNetYear === 'number' ? plan.letterNetYear : r2(b * 14 + c * 12 + meal * 11);
      const mensal = r2(annual / 12);
      const ssub = r2(b + c + meal);
      return {
        label, sub, letter, irsBase, irsCommission, ss: r2(ssBase + ssCom), deductions: deductionList.map((d) => d.amount),
        total: r2(irsBase + irsCommission + ssBase + ssCom + dedTotal), annual, mensal, ssub, bank: r2(b + c), meal, extra: b,
        planoAnual: r2(annual + extraA * 12), planoMensal: r2(mensal + extraA), planoSsub: r2(ssub + extraA),
      };
    };
    columns.push(make('Offer letter', 'normal IRS', 1, true));
    for (const st of stages) columns.push(make(span(st), `${st.pct}% tax-free`, (100 - st.pct) / 100, false));
    if (columns.length < 2) return null;
    return { source: 'letter', columns, deductionLabels: deductionList.map((d) => d.label), allowance: extraA };
  }

  // No letter figures: the same salary without IRS Jovem, then each stage, with the official IRS tables
  const official = (label, sub, year, jovem, letter) => {
    const c = column(label, sub, year, jovem);
    const dedTotal = r2(c.deductions.reduce((a, d) => a + d, 0));
    const annual = r2(c.bank * 12 + c.extras[0] + c.extras[1] + meal * 11);
    const mensal = r2(annual / 12);
    const ssub = r2(c.bank + meal);
    return {
      label, sub, letter, irsBase: c.irs, irsCommission: null, ss: c.ss, deductions: c.deductions, total: r2(c.irs + c.ss + dedTotal),
      annual, mensal, ssub, bank: c.bank, meal, extra: c.extra ?? c.extras[0],
      planoAnual: r2(annual + extraA * 12), planoMensal: r2(mensal + extraA), planoSsub: r2(ssub + extraA),
    };
  };
  columns.push(official('Without IRS Jovem', 'normal IRS', thisYear, { eligible: false, pct: 0 }, true));
  for (const st of stages) columns.push(official(span(st), `${st.pct}% tax-free`, st.years[0], { eligible: true, pct: st.pct, index: st.index }, false));
  if (columns.length < 2) return null;
  return { source: 'tables', columns, deductionLabels: deductionList.map((d) => d.label), allowance: extraA };
}
